import React from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonLoading,
  useIonPopover,
} from "@ionic/react";
import Masonry from "react-masonry-css";
import { exportZip } from "../../lib/saveCanvas";
import { useState } from "react";
import { checkmarkCircle, ellipseOutline } from "ionicons/icons";

const VideoConverterGallery: React.FC<{
  gallery: { filename: string; blob: Blob; checked?: boolean }[];
  handleProjectStore: (e) => void;
  handleDismiss: () => void;
}> = ({ gallery: currentGallery, handleProjectStore, handleDismiss }) => {
  const [gallery, setGallery] = useState(currentGallery || []);
  const [isSelecting, setIsSelecting] = useState(false);
  const [presentLoading, dismissLoading] = useIonLoading();
  const isAllSelected = gallery.every((img) => img.checked);
  const selectNum = gallery.filter((img) => img.checked).length;
  const selectedImages = gallery.filter((img) => !img.checked);
  const notSelectedImages = gallery.filter((img) => img.checked);

  const [presentPop, dismissPop] = useIonPopover(VCPopOver, {
    handleDelete: () => handleDelete(),
    dismissPop: () => dismissPop(),
  });

  const handleDelete = () => {
    handleProjectStore(selectedImages);
    setGallery(selectedImages);
    if (selectedImages.length === 0) {
      setIsSelecting(false);
    }
  };
  const breakpointColumnsObj = {
    default: 2,
    480: 1,
  };
  const handelDownloadImages = async (images) => {
    presentLoading();
    await exportZip(
      "video-converter",
      "video-snapshot",
      images.map((img) => img.blob)
    ).then(() => {
      setTimeout(() => {
        dismissLoading();
        handleDismiss();
      }, 300);
    });
  };
  const handleSelect = (checked, filename) => {
    setGallery(
      gallery.map((img) =>
        img.filename === filename ? { ...img, checked } : { ...img }
      )
    );
  };
  const handleSelectAll = () => {
    setGallery(gallery.map((img) => ({ ...img, checked: true })));
  };
  const handleSelectNone = () => {
    setGallery(gallery.map((img) => ({ ...img, checked: false })));
  };
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          {isSelecting && (
            <IonButtons slot="secondary">
              {isAllSelected ? (
                <IonButton onClick={handleSelectNone}>Unselect All</IonButton>
              ) : (
                <IonButton onClick={handleSelectAll}>Select All</IonButton>
              )}
            </IonButtons>
          )}
          <IonButtons slot="primary">
            {isSelecting ? (
              <IonButton
                onClick={() => setIsSelecting(false)}
                className="!font-semibold"
              >
                Done
              </IonButton>
            ) : (
              <IonButton
                onClick={() => setIsSelecting(true)}
                className="!font-semibold"
                disabled={gallery.length === 0}
              >
                Select
              </IonButton>
            )}
          </IonButtons>
          <IonTitle>Snapshots</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="no-scroll">
        {gallery.length === 0 && (
          <div className="w-full flex justify-center items-center max-w-7xl mx-auto h-full">
            <div className="w-full">
              <img
                src="/assets/images/image-bg.svg"
                className="max-w-[200px] w-3/5 px-4 opacity-20 mx-auto mb-6
                    "
                alt="No items found"
              />
              <p className="opacity-50 text-center">
                No images found, let's start taking a few!
              </p>
            </div>
          </div>
        )}
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className="flex max-w-7xl mx-auto gap-4 md:gap-6 my-4 md:my-6 sm:px-6"
          columnClassName="flex flex-col gap-4 md:gap-6"
        >
          {gallery.length > 0 &&
            gallery.map((image) => (
              <VideoConverterImage
                key={`image.${image.filename}`}
                {...{ image, handleSelect, isSelecting }}
              />
            ))}
        </Masonry>
      </IonContent>
      <IonFooter>
        <IonToolbar className="!px-0">
          {isSelecting ? (
            <div className="grid grid-cols-2 gap-2 px-2">
              <IonButton
                color="secondary"
                onClick={(e) =>
                  presentPop({
                    event: e.nativeEvent,
                    arrow: true,
                  })
                }
                disabled={selectNum === 0}
              >
                Delete...
              </IonButton>
              <IonButton
                onClick={() => handelDownloadImages(notSelectedImages)}
                disabled={selectNum === 0}
              >
                Download Selected ({selectNum})
              </IonButton>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 px-2">
              <IonButton color="secondary" onClick={handleDismiss}>
                Dismiss
              </IonButton>
              <IonButton
                onClick={() => handelDownloadImages(gallery)}
                disabled={gallery.length === 0}
              >
                Download All
              </IonButton>
            </div>
          )}
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};
const VideoConverterImage: React.FC<{
  image;
  handleSelect: (checked, filename) => void;
  isSelecting: boolean;
}> = ({ image, handleSelect, isSelecting }) => {
  const src = (blob) => URL.createObjectURL(blob);
  return (
    <label
      className="w-full relative"
      htmlFor={`image.input.${image.filename}`}
    >
      <input
        name={`image.input[${image.filename}]`}
        type="checkbox"
        id={`image.input.${image.filename}`}
        checked={image.checked}
        onChange={(e) => handleSelect(e.target.checked, image.filename)}
        aria-hidden
        className="hidden"
        disabled={!isSelecting}
      />
      {isSelecting && (
        <IonIcon
          className="absolute right-2 top-2 z-[2]"
          color="primary"
          size="large"
          icon={ellipseOutline}
        />
      )}
      {isSelecting && image.checked && (
        <IonIcon
          className="absolute right-2 top-2 z-[2]"
          color="primary"
          size="large"
          icon={checkmarkCircle}
        />
      )}
      <img
        src={src(image.blob)}
        alt="generated"
        className={`rounded-md ${
          isSelecting && image.checked ? "opacity-75" : ""
        }`}
      />
    </label>
  );
};
const VCPopOver = ({ handleDelete, dismissPop }) => {
  return (
    <IonContent>
      <IonButton
        color="danger"
        expand="block"
        onClick={() => {
          handleDelete();
          dismissPop();
        }}
      >
        Delete
      </IonButton>
    </IonContent>
  );
};
export default VideoConverterGallery;
